import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useState } from "react";
import { Star, GripVertical, ChevronRight } from "lucide-react";
import { TID } from "@/constants/testIds";
import { toast } from "sonner";
import { Link } from "react-router-dom";

export default function PipelinePage() {
    const qc = useQueryClient();
    const q = useQuery({ queryKey: ["pipeline"], queryFn: () => api.get("/pipeline").then((r) => r.data) });
    const move = useMutation({
        mutationFn: ({ client_id, stage }) => api.post("/pipeline/move", { client_id, stage }),
        onSuccess: () => qc.invalidateQueries({ queryKey: ["pipeline"] }),
        onError: () => toast.error("Errore aggiornamento pipeline"),
    });
    const [dragId, setDragId] = useState(null);

    if (q.isLoading || !q.data) return <div className="text-textmuted">Caricamento pipeline…</div>;

    const stages = q.data.stages;
    const board = q.data.board;

    const onDrop = (stage) => (e) => {
        e.preventDefault();
        if (dragId) {
            move.mutate({ client_id: dragId, stage });
            setDragId(null);
            toast.success("Card spostata.");
        }
    };
    const totalRevenue = Object.entries(board).reduce((s, [k, arr]) => s + arr.length, 0);

    return (
        <div className="space-y-6 pv-enter">
            <div className="flex items-end justify-between">
                <div>
                    <div className="pv-eyebrow mb-2">Sales Pipeline</div>
                    <h1 className="pv-serif text-4xl tracking-tight">{totalRevenue} <span className="text-textmuted">opportunità aperte</span></h1>
                </div>
                <div className="text-[11.5px] text-textsubtle">Trascina le card tra le colonne per aggiornare lo stato.</div>
            </div>

            <div className="flex gap-4 overflow-x-auto pv-scroll pb-4 -mx-2 px-2">
                {stages.map((stage) => (
                    <div
                        key={stage}
                        data-testid={TID.pipelineColumn(stage)}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={onDrop(stage)}
                        className="w-[290px] flex-shrink-0 pv-card p-3.5 bg-surface2/50"
                    >
                        <div className="flex items-center justify-between mb-3 px-1">
                            <div className="flex items-center gap-2">
                                <div className={`h-2 w-2 rounded-full ${stageColor(stage)}`} />
                                <span className="text-[12.5px] font-medium">{stage}</span>
                            </div>
                            <span className="text-[10.5px] pv-mono text-textsubtle">{board[stage].length}</span>
                        </div>
                        <div className="space-y-2 min-h-[60px]">
                            {board[stage].map((c) => (
                                <Link
                                    to={`/crm/${c.id}`}
                                    key={c.id}
                                    data-testid={TID.pipelineCard(c.id)}
                                    draggable
                                    onDragStart={() => setDragId(c.id)}
                                    onDragEnd={() => setDragId(null)}
                                    className="pv-card p-3 pv-hover cursor-grab active:cursor-grabbing block"
                                >
                                    <div className="flex items-start gap-2">
                                        <GripVertical size={13} strokeWidth={1.5} className="text-textsubtle mt-0.5 flex-shrink-0" />
                                        <div className="flex-1 min-w-0">
                                            <div className="text-[13px] font-medium truncate">{c.name}</div>
                                            <div className="text-[11px] text-textsubtle truncate">{c.studio}</div>
                                            <div className="flex items-center justify-between mt-2">
                                                <span className="text-[10.5px] text-textmuted">{c.city} · {c.source}</span>
                                                <div className="flex items-center gap-0.5">
                                                    {Array.from({ length: 5 }).map((_, i) => (
                                                        <Star key={i} size={9} strokeWidth={1.5}
                                                              className={i < c.stars ? "fill-warning text-warning" : "text-textsubtle/30"} />
                                                    ))}
                                                </div>
                                            </div>
                                            <div className="mt-2 h-1 bg-surface2 rounded-full overflow-hidden">
                                                <div className="h-full bg-primary" style={{ width: `${c.lead_score}%` }} />
                                            </div>
                                        </div>
                                    </div>
                                </Link>
                            ))}
                            {board[stage].length === 0 && (
                                <div className="text-[11px] text-textsubtle text-center py-6">Trascina qui</div>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function stageColor(s) {
    const map = {
        "Lead": "bg-textsubtle", "Contattato": "bg-azure", "Demo": "bg-primary",
        "Follow-up": "bg-warning", "Trattativa": "bg-opportunity",
        "Contratto": "bg-primary", "Cliente": "bg-success",
    };
    return map[s] || "bg-textsubtle";
}
